import { useEffect, useState, useMemo } from "react";

export const useDebounce = (value, delay = 1000) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

export const useOptimizedSelect = (
  options = [],
  {
    limit = 250,
    debounceMs = 1000,
    labelKey = "name",
    filterKey = "",
    extraKey= "",
    valueKey = "id"
  } = {}
) => {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebounce(inputValue, debounceMs);

  useEffect(() => {
    if (inputValue !== debouncedSearch) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [inputValue, debouncedSearch]);

  const visibleOptions = useMemo(() => {
    if(!options) return [];
    if (!debouncedSearch) return options.slice(0, limit);
    const keyword = debouncedSearch.toLowerCase();
    return options.filter(opt =>{
      const value = extraKey 
        ? opt[extraKey]?.[filterKey] 
        : opt[filterKey];
      return (opt[labelKey]?.toLowerCase().includes(keyword) || 
      (filterKey && value?.toLowerCase().includes(keyword)))
    });
    
  }, [options, debouncedSearch, limit, labelKey]);

  const getOptionLabel = (option) => option[labelKey];
  const getOptionValue = (option) => option[valueKey];

  return {
    options: visibleOptions,
    onInputChange: (value) => {
      setInputValue(value);
      return value; // IMPORTANT for react-select
    },
    getOptionLabel,
    getOptionValue,
    filterOption: () => true,
    isLoading,
  };
};

export const useSearchFilter = (
  items = [],
  {
    limit = 250,
    debounceMs = 500,
    searchKeys = ["name"]
  } = {}
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, debounceMs);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    
    let filtered = items;
    if (debouncedSearch) {
      const keyword = debouncedSearch.toLowerCase();
      filtered = items.filter(item =>
        searchKeys.some(key => 
          item[key]?.toString().toLowerCase().includes(keyword)
        )
      );
    }

    // If no search, limit the results for performance
    if (!debouncedSearch && limit > 0) {
      return filtered.slice(0, limit);
    }

    // If searching, we might still want a limit or show all matches
    // User requested "show only 250 at first then i search and filter items from all the state"
    // This implies full search results are fine, but initial view is limited.
    return filtered;
  }, [items, debouncedSearch, limit, searchKeys]);

  return {
    searchTerm,
    setSearchTerm,
    filteredItems,
    totalCount: items?.length || 0,
    displayedCount: filteredItems?.length || 0
  };
};